import React, { useMemo } from "react";

import Checkbox from "antd/es/checkbox";
import { ColumnType } from "antd/es/table";
import { IntlShape } from "react-intl";

import Activity from "@mapmycustomers/shared/types/entity/Activity";
import Company from "@mapmycustomers/shared/types/entity/Company";
import Deal from "@mapmycustomers/shared/types/entity/Deal";
import Person from "@mapmycustomers/shared/types/entity/Person";
import { stopEvents } from "@mapmycustomers/shared/util/browser";

import ActivityAssignee from "@app/component/activity/ActivityAssignee";
import ActivityDate from "@app/component/activity/ActivityDate";
import ActivityFiles from "@app/component/activity/ActivityFiles";
import ActivityName from "@app/component/activity/ActivityName";
import ActivityNote from "@app/component/activity/ActivityNote";

import SimpleActions from "../component/ActivityListPage/component/SimpleActions";
import { toggleActivitySelection } from "../store/actions";

interface UseColumnsArgs {
  areAllSelected: boolean;
  intl: IntlShape;
  isLoading: boolean;
  isReadOnlyMember: boolean;
  onCompanyClick: (companyId: Company["id"]) => void;
  onDealClick: (dealId: Deal["id"]) => void;
  onPersonClick: (personId: Person["id"]) => void;
  selected: Activity[];
  toggleActivitySelection: typeof toggleActivitySelection;
  toggleSelectAll: () => void;
  updateNote: (data: { activity: Activity; onSuccess: () => void; updated?: boolean }) => void;
  updateNoteLoadingIds: Activity["id"][];
  width: number;
}

const useColumns = ({
  areAllSelected,
  intl,
  isLoading,
  isReadOnlyMember,
  onCompanyClick,
  onDealClick,
  onPersonClick,
  selected,
  toggleActivitySelection,
  toggleSelectAll,
  updateNote,
  updateNoteLoadingIds,
  width,
}: UseColumnsArgs): ColumnType<Activity>[] => {
  return useMemo(
    () => [
      ...(isReadOnlyMember
        ? []
        : [
            {
              dataIndex: "checkbox",
              key: "checkbox",
              render: (_: any, activity: Activity) => (
                <Checkbox
                  checked={selected.some((s) => s.id === activity.id)}
                  onClick={(e) => {
                    stopEvents(e);
                    toggleActivitySelection(activity);
                  }}
                />
              ),
              title: (
                <Checkbox
                  checked={!isLoading && areAllSelected}
                  disabled={isLoading}
                  onClick={() => toggleSelectAll()}
                />
              ),
              width: isReadOnlyMember ? width * 0.05 : 64,
            },
          ]),
      {
        dataIndex: "details",
        key: "details",
        render: (_: any, activity: Activity) => (
          <ActivityName
            activity={activity}
            onCompanyClick={onCompanyClick}
            onDealClick={onDealClick}
            onPersonClick={onPersonClick}
          />
        ),
        title: intl.formatMessage({
          id: "activity.listView.table.column.details",
          defaultMessage: "Activity Details",
          description: "Name of a details column in activity table on activity page",
        }),
        width: width * (isReadOnlyMember ? 0.3 : 0.2),
      },
      {
        dataIndex: "note",
        key: "note",
        render: (_: any, activity: Activity) => (
          <ActivityNote
            activity={activity}
            loading={updateNoteLoadingIds.includes(activity.id)}
            onSave={updateNote}
          />
        ),
        title: intl.formatMessage({
          id: "activity.listView.table.column.note",
          defaultMessage: "Note",
          description: "Name of a note column in activity table on activity page",
        }),
        width: width * 0.1,
      },
      ...(isReadOnlyMember
        ? []
        : [
            {
              dataIndex: "file",
              key: "files",
              render: (_: any, activity: Activity) => <ActivityFiles activity={activity} />,
              title: intl.formatMessage({
                id: "activity.listView.table.column.file",
                defaultMessage: "File",
                description: "Name of a file column in activity table on activity page",
              }),
              width: width * 0.1,
            },
          ]),
      {
        dataIndex: "assignee",
        key: "assignee",
        render: (_: any, activity: Activity) => <ActivityAssignee activity={activity} />,
        title: intl.formatMessage({
          id: "activity.listView.table.column.assignee",
          defaultMessage: "Assignee",
          description: "Name of an assignee column in activity table on activity page",
        }),
        width: width * 0.1,
      },
      {
        dataIndex: "date",
        key: "date",
        render: (_: any, activity: Activity) => <ActivityDate activity={activity} />,
        title: intl.formatMessage({
          id: "activity.listView.table.column.date",
          defaultMessage: "Date",
          description: "Name of a date column in activity table on activity page",
        }),
        width: width * 0.15,
      },
      ...(isReadOnlyMember
        ? []
        : [
            {
              dataIndex: "quickActions",
              key: "quickActions",
              render: (_: any, activity: Activity) => <SimpleActions activity={activity} />,
            },
          ]),
    ],
    [
      intl,
      isLoading,
      onCompanyClick,
      onDealClick,
      onPersonClick,
      isReadOnlyMember,
      selected,
      toggleActivitySelection,
      toggleSelectAll,
      updateNote,
      updateNoteLoadingIds,
      width,
      areAllSelected,
    ]
  );
};

export default useColumns;
